import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { authApi, saveTokens, getAccessToken, clearTokens, type AuthResponse } from '@/lib/api';

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(() => Boolean(getAccessToken()));

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      return;
    }

    authApi.getMe().then((response) => {
      if (response.data) {
        // Redirect to root which handles business-aware routing
        navigate('/');
      } else {
        clearTokens();
        setCheckingAuth(false);
      }
    });
  }, [navigate]);

  const finishAuth = (auth: AuthResponse) => {
    saveTokens(auth.tokens);
    if (auth.user.businesses.length === 0) {
      navigate('/onboarding');
    } else {
      const firstBusiness = auth.user.businesses[0];
      navigate(`/${firstBusiness.id}/dashboard`);
    }
  };

  const submitSignup = async (normalizedEmail: string) => {
    const response = await authApi.registration({
      email: normalizedEmail,
      password1: password,
      password2: password,
    });

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.data) {
      finishAuth(response.data);
    }
  };

  const submitLogin = async (normalizedEmail: string) => {
    const response = await authApi.login(normalizedEmail, password);

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.data) {
      finishAuth(response.data);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const normalizedEmail = email.trim();
    const emailCheck = await authApi.checkEmail(normalizedEmail);

    if (emailCheck.data?.available) {
      await submitSignup(normalizedEmail);
    } else {
      await submitLogin(normalizedEmail);
    }

    setLoading(false);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError('');
      const response = await authApi.googleLogin({
        access_token: tokenResponse.access_token,
      });
      setLoading(false);

      if (response.error) {
        setError(response.error);
        return;
      }

      if (response.data) {
        finishAuth(response.data);
      }
    },
    onError: () => {
      setError('Google login failed. Please try again.');
    },
  });

  if (checkingAuth) {
    return null;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/header.svg" alt="InvoiceOwl" className="h-14 mx-auto" />
          </div>

          <Card>
            <CardHeader className="text-center pb-4">
              <h2 className="text-2xl font-semibold">Welcome back</h2>
              <p className="text-muted-foreground mt-1">Sign in or create your account</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Continuing...' : 'Continue'}
                </Button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <Button variant="outline" type="button" className="w-full" onClick={() => googleLogin()} disabled={loading}>
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Google
                  </Button>
                </div>
              </div>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary hover:underline font-medium">
                  Sign up for free
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Social Proof & Benefits */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-12 items-center justify-center relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-between h-full max-w-md py-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Get paid 4x faster with<br />professional invoices
            </h2>
            <p className="text-muted-foreground">
              Join thousands of businesses simplifying their billing
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm w-full">
            <div className="flex gap-0.5 mb-4">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-4 h-4 text-amber-400 fill-current" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <p className="text-foreground leading-relaxed">
              "I use InvoiceOwl for my pest control business and it's made my life so much easier, as I can invoice the customers on-site straight after completing a job."
            </p>
            <div className="mt-5 flex items-center gap-3">
              <img
                src="/danny-hutchinson.webp"
                alt="Danny Hutchinson"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="font-medium text-sm text-foreground">Danny Hutchinson</p>
                <p className="text-muted-foreground text-xs">CEO of PreventiPest, Colorado</p>
              </div>
            </div>
          </div>

          <div className="flex gap-8 text-center pt-10 border-t border-border">
            <div>
              <p className="text-2xl font-bold text-primary">50,000+</p>
              <p className="text-sm text-muted-foreground">Businesses trust us</p>
            </div>
            <div className="w-px bg-border" />
            <div>
              <p className="text-2xl font-bold text-primary">3 days</p>
              <p className="text-sm text-muted-foreground">Avg. time to get paid</p>
            </div>
            <div className="w-px bg-border" />
            <div>
              <p className="text-2xl font-bold text-primary">$2B+</p>
              <p className="text-sm text-muted-foreground">Invoices processed</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
